import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  ContentChildren,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  SimpleChanges,
  forwardRef
} from '@angular/core';

import { NgxResizeObserverService } from 'ngx-resize-observer';
import { Observable, Subject, distinctUntilChanged, map, merge, takeUntil } from 'rxjs';

import {
  BreakpointService,
  DirectionService,
  ResizeService,
  ScrollIntoViewBlock,
  ScrollService,
  debounceTimeAfterFirst
} from '@hdms/shared/util-core';

import { ScrollbarComponent } from './scrollbar.component';
import { ScrollCoords } from './types/scroll-coords';
import { ScrollDirection } from './types/scroll-direction';
import { Scrollable } from './types/scrollable';

export const SCROLLABLE_HIDDEN_CLASS = {
  [ScrollDirection.HORIZONTAL]: 'scrollbar-horizontal-hidden',
  [ScrollDirection.VERTICAL]: 'scrollbar-vertical-hidden'
};

export const SCROLLABLE_SHOWN_CLASS = {
  [ScrollDirection.HORIZONTAL]: 'scrollbar-horizontal-shown',
  [ScrollDirection.VERTICAL]: 'scrollbar-vertical-shown'
};

@Directive({
  selector: '[shrdUicScrollbar]',
  exportAs: 'shrdUicScrollbar'
})
export class ScrollbarDirective implements Scrollable, OnInit, AfterContentInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() scrollBarHorizontalRef: ScrollbarComponent;
  @Input() scrollBarVerticalRef: ScrollbarComponent;

  @Input() scrollContentRef?: Element | string;
  @Input() update?: Observable<void> | Subject<void>;
  @Input() initialScrollTo?: string;

  // trigger ScrollService to update all observers listening to global scroll-events
  @Input() triggerGlobalScroll = false;

  @HostBinding('class.show-scrollbar-on-mobile')
  @Input()
  visibleOnMobile = false;

  private visiblePercentage: ScrollCoords;
  private scrollLeft = 0;
  private scrollTop = 0;
  private horizontalOverScroll = 0;
  private verticalOverScroll = 0;
  private enabled = true;

  private readonly isRtl: boolean;
  private readonly element: HTMLElement;
  private readonly unsubscribe$ = new Subject<void>();
  private readonly observeResize$ = new Subject<string>();
  private readonly resizeObserverTargets: Element[] = [];
  private readonly scrollbarRefs = {
    [ScrollDirection.HORIZONTAL]: null as ScrollbarComponent,
    [ScrollDirection.VERTICAL]: null as ScrollbarComponent
  };

  @ContentChildren(forwardRef(() => ScrollbarComponent))
  private readonly scrollbarChildren: QueryList<ScrollbarComponent>;

  @HostBinding('class.scrollable')
  private readonly hostClass = true;

  @HostBinding('class.scrollable-horizontal')
  public hasHorizontalScroller: boolean;

  @HostBinding('class.scrollable-vertical')
  public hasVerticalScroller: boolean;

  @HostBinding('class.scrollable-x-start')
  public scrollXStart: boolean;

  @HostBinding('class.scrollable-x-end')
  public scrollXEnd: boolean;

  @HostBinding('class.scrollable-y-start')
  public scrollYStart: boolean;

  @HostBinding('class.scrollable-y-end')
  public scrollYEnd: boolean;

  @HostListener('scroll')
  onScroll() {
    this.updateScrollPosition();
    if (this.triggerGlobalScroll) {
      this.scrollService.triggerScroll();
    }
    if (this.enabled) {
      const scrollPosition = this.getScrollPosition();
      const overScroll = this.getOverScroll();

      [ScrollDirection.HORIZONTAL, ScrollDirection.VERTICAL].forEach(dir => {
        const ref = this.scrollbarRefs[dir];
        if (ref && !ref.isHidden) {
          ref.setTrackCompensation(scrollPosition);
          ref.setThumbPosition(scrollPosition[dir] / overScroll[dir]);
        }
      });
    }
  }

  constructor(
    private readonly renderer: Renderer2,
    private readonly scrollService: ScrollService,
    private readonly resizeObserver: NgxResizeObserverService,
    private readonly resizeService: ResizeService,
    private readonly cdr: ChangeDetectorRef,
    directionService: DirectionService,
    breakpointService: BreakpointService,
    { nativeElement }: ElementRef<HTMLElement>
  ) {
    this.element = nativeElement;
    this.isRtl = directionService.isRtl;

    breakpointService.isMobileView$
      .pipe(
        map(isMobile => !isMobile),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(enabled => {
        this.enabled = enabled || this.visibleOnMobile;

        if (this.hasHorizontalScroller) {
          !enabled && this.setScrollPosition(0, ScrollDirection.HORIZONTAL);
          this.onScroll();
        }
      });
  }

  ngOnInit(): void {
    if (this.scrollContentRef) {
      const scrollCntRef =
        typeof this.scrollContentRef === 'string'
          ? Array.from(this.element.querySelectorAll(this.scrollContentRef))
          : [this.scrollContentRef];
      if (scrollCntRef.length) {
        this.resizeObserverTargets.push(...scrollCntRef);
      }
    }

    this.resizeObserverTargets.push(this.element);

    this.resizeObserverTargets.forEach(tgt => {
      this.resizeObserver.observe(
        tgt,
        ({ contentRect: { width, height } }) => {
          // create a unique hash depending on active scroll directions
          // so the result wil be filtered correctly by distinctUntilChange below
          const hash = `${this.hasVerticalScroller ? height : 0}${this.hasHorizontalScroller ? width : 0}`;
          this.observeResize$.next(hash);
        },
        'border-box'
      );
    });

    merge(this.observeResize$.pipe(distinctUntilChanged()), this.resizeService.onResize$)
      .pipe(debounceTimeAfterFirst(50), takeUntil(this.unsubscribe$))
      .subscribe(this.resetScrollDimensions.bind(this));
  }

  ngAfterContentInit(): void {
    (this.scrollbarChildren?.toArray() || []).forEach(scrollBarChild => {
      const direction = scrollBarChild.horizontal ? ScrollDirection.HORIZONTAL : ScrollDirection.VERTICAL;
      this.updateScrollBarReferences(scrollBarChild, { direction });
    });
  }

  ngAfterViewInit(): void {
    this.scrollToTarget(this.initialScrollTo);
  }

  ngOnChanges({ update, visibleOnMobile, scrollBarHorizontalRef, scrollBarVerticalRef }: SimpleChanges): void {
    /*
     * Subscribe to update observable if one was defined.
     * For now subscribe only once (even if input is changed).
     * This allows to trigger an update to the scrollbar from
     * the outside.
     */
    if (update?.firstChange) {
      this.update.pipe(takeUntil(this.unsubscribe$)).subscribe(this.updateScrollbar.bind(this));
    }

    if (scrollBarHorizontalRef) {
      this.updateScrollBarReferences(this.scrollBarHorizontalRef, {
        direction: ScrollDirection.HORIZONTAL,
        external: true
      });
    }

    if (scrollBarVerticalRef) {
      this.updateScrollBarReferences(this.scrollBarVerticalRef, {
        direction: ScrollDirection.VERTICAL,
        external: true
      });
    }

    if (visibleOnMobile && this.hasHorizontalScroller) {
      this.enabled = this.visibleOnMobile;
      this.scrollBarHorizontalRef.visibleOnMobile = this.visibleOnMobile;
    }
  }

  private updateScrollBarReferences(
    scrollBar: ScrollbarComponent | null,
    { direction, external = false }: { direction: ScrollDirection; external?: boolean }
  ): void {
    this.scrollbarRefs[direction]?.unbindReference();
    this.scrollbarRefs[direction] = scrollBar;

    if (scrollBar) {
      scrollBar.bindReference(this);
      scrollBar.external = external;
      scrollBar.horizontal = direction === ScrollDirection.HORIZONTAL;
    }

    if (direction === ScrollDirection.HORIZONTAL) {
      this.hasHorizontalScroller = !!scrollBar;
    } else {
      this.hasVerticalScroller = !!scrollBar;
    }
  }

  ngOnDestroy(): void {
    Object.values(this.scrollbarRefs).forEach(scrollBar => scrollBar?.unbindReference());
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.observeResize$.complete();
    this.resizeObserverTargets.forEach(tgt => {
      this.resizeObserver.unobserve(tgt);
    });
  }

  public show(dir: ScrollDirection): void {
    this.renderer.removeClass(this.element, SCROLLABLE_HIDDEN_CLASS[dir]);
    this.renderer.addClass(this.element, SCROLLABLE_SHOWN_CLASS[dir]);
  }

  public hide(dir: ScrollDirection): void {
    this.renderer.addClass(this.element, SCROLLABLE_HIDDEN_CLASS[dir]);
    this.renderer.removeClass(this.element, SCROLLABLE_SHOWN_CLASS[dir]);
  }

  public setScrollPositionByExact(percentage: number, dir: ScrollDirection, immediate = false): void {
    const scrollPosition = this.getOverScroll()[dir] * percentage;
    this.setScrollPosition(scrollPosition, dir);
    immediate && this.updateScrollbarComponent();
  }

  public setScrollPositionByDelta(delta: number, dir: ScrollDirection): void {
    let scrollPosition = this.getScrollPosition()[dir] + delta;
    const overScroll = this.getOverScroll()[dir];

    if (dir === ScrollDirection.HORIZONTAL && this.isRtl) {
      if (scrollPosition < -overScroll) {
        scrollPosition = -overScroll;
      } else if (scrollPosition > 0) {
        scrollPosition = 0;
      }
    } else {
      if (scrollPosition > overScroll) {
        scrollPosition = overScroll;
      } else if (scrollPosition < 0) {
        scrollPosition = 0;
      }
    }
    this.setScrollPosition(scrollPosition, dir);
  }

  private setScrollPosition(position: number, dir: ScrollDirection): void {
    if (dir === ScrollDirection.HORIZONTAL) {
      this.element.scrollLeft = this.scrollLeft = position;
    } else {
      this.element.scrollTop = this.scrollTop = position;
    }
  }

  private setDimensions(): void {
    const offsetWidth = this.element.offsetWidth;
    const offsetHeight = this.element.offsetHeight;
    const scrollWidth = this.element.scrollWidth;
    const scrollHeight = this.element.scrollHeight;
    this.horizontalOverScroll = scrollWidth - offsetWidth;
    this.verticalOverScroll = scrollHeight - offsetHeight;
    this.visiblePercentage = {
      [ScrollDirection.HORIZONTAL]: offsetWidth / scrollWidth,
      [ScrollDirection.VERTICAL]: offsetHeight / scrollHeight
    };
    this.updateScrollPosition();
    this.cdr.markForCheck();
  }

  private getScrollPosition(): ScrollCoords {
    return {
      [ScrollDirection.HORIZONTAL]: this.scrollLeft,
      [ScrollDirection.VERTICAL]: this.scrollTop
    };
  }

  private updateScrollPosition(): void {
    this.scrollLeft = this.element.scrollLeft;
    this.scrollTop = this.element.scrollTop;

    if (this.hasHorizontalScroller) {
      this.scrollXStart = this.scrollLeft + (this.isRtl ? this.horizontalOverScroll : 0) <= 5;
      this.scrollXEnd = this.scrollLeft >= (this.isRtl ? 0 : this.horizontalOverScroll) - 5;
    }

    if (this.hasVerticalScroller) {
      this.scrollYStart = this.scrollTop <= 5;
      this.scrollYEnd = this.scrollTop >= this.verticalOverScroll - 5;
    }
  }

  private getOverScroll(): ScrollCoords {
    return {
      [ScrollDirection.HORIZONTAL]: this.horizontalOverScroll,
      [ScrollDirection.VERTICAL]: this.verticalOverScroll
    };
  }

  private getThumbPosition(): ScrollCoords {
    const pos = this.getScrollPosition();
    const overScroll = this.getOverScroll();
    return {
      [ScrollDirection.HORIZONTAL]: pos[ScrollDirection.HORIZONTAL] / overScroll[ScrollDirection.HORIZONTAL],
      [ScrollDirection.VERTICAL]: pos[ScrollDirection.VERTICAL] / overScroll[ScrollDirection.VERTICAL]
    };
  }

  private updateScrollbar() {
    this.setDimensions();
    this.updateScrollbarComponent();
  }

  private updateScrollbarComponent(): void {
    if (this.enabled) {
      const status = {
        visiblePercentage: this.visiblePercentage,
        thumbPosition: this.getThumbPosition()
      };

      Object.values(this.scrollbarRefs).forEach(ref => {
        ref && ref.update(status);
      });
    }
  }

  private resetScrollDimensions(): void {
    if (this.enabled) {
      // hide internal scrollbar to properly calculate the update
      Object.values(this.scrollbarRefs).forEach(ref => {
        ref && !ref.external && ref.hide(false);
      });

      this.updateScrollbar();

      const pos = this.getScrollPosition();
      const overScroll = this.getOverScroll();

      [ScrollDirection.HORIZONTAL, ScrollDirection.VERTICAL].forEach(dir => {
        // in case content width/height is less than before,
        // scroll-position needs a reset to max avail. overScroll
        if (pos[dir] > overScroll[dir]) {
          this.setScrollPosition(overScroll[dir], dir);
        }
      });
    } else {
      this.setDimensions();
    }
  }

  private scrollToTarget(targetSelector: string): void {
    if (targetSelector) {
      const target: HTMLElement = this.element.querySelector(targetSelector);
      if (target) {
        this.scrollService.scrollTo(target, ScrollIntoViewBlock.START);
      }
    }
  }
}
